import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { cn } from "~/lib/utils";

/**
 * `default`/`primary` eran el azul del fork (#3f58b6). Ahora salen rojas SOLAS, porque
 * `--primary-700` y `--contrast` son los de la marca (index.css): #C0392B con label blanco
 * en claro, #DD7160 con label oscuro en oscuro (el mismo criterio que el CTA de la marca).
 *
 * No son alias de `brand` a propósito: ~50 `<Button>` sin variante le pisan el fondo con
 * `className` (la toolbar del editor, los "Cancelar" transparentes de los formularios de
 * clase, "Vista previa"…). `brand` trae `hover:bg-landing-red-hover` y la sombra roja, que
 * un `bg-transparent` NO anula: esos botones se pondrían rojos al pasar el mouse. Por eso el
 * default conserva la mecánica de siempre (fondo + `hover:opacity-90`) y el CTA con sombra y
 * elevación queda para quien lo pide explícito con `variant="brand"`.
 *
 * `outline` sí es alias de `brand-outline`: no tiene hover de fondo que filtrar, y el texto
 * azul del fork pasa a la tinta de la marca.
 */
const brandSolid =
  "bg-landing-red text-white shadow-landing-red hover:-translate-y-0.5 hover:bg-landing-red-hover hover:shadow-landing-red-hover";
const brandOutline =
  "border border-landing-border bg-white text-landing-text hover:border-landing-red hover:text-landing-red";

const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-lg text-sm font-medium ring-offset-background transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default: "bg-primary-700 text-contrast hover:opacity-90",
        primary: "bg-primary-700 text-contrast hover:opacity-90",
        brand: brandSolid,
        // Más profundo que el CTA (ver `--destructive` en index.css) y con SU label: antes
        // usaba `primary-foreground`, que en oscuro ahora es tinta oscura.
        destructive: "bg-destructive text-destructive-foreground hover:opacity-90",
        outline: brandOutline,
        "brand-outline": brandOutline,
        // Acción secundaria silenciosa: stone, no un segundo rojo.
        secondary: "bg-secondary-600 text-contrast hover:opacity-90",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-lg px-3",
        lg: "h-11 rounded-lg px-8",
        icon: "size-10",
        xs: "h-6 rounded-md p-1 text-xs",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        data-cuelume-press={props.disabled ? undefined : ""}
        data-cuelume-release={props.disabled ? undefined : ""}
        {...props}
      />
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
